import React, { useState, useEffect, useMemo } from 'react';
import { getCountry } from './utils/countryUtils';
import { detectIP, encodeIP } from './utils/ipUtils';
import { ScoresAdapter } from './adapters/ScoresAdapter';
import { UserAdapter } from './adapters/UserAdapter';
import Leaderboard from './components/Leaderboard';
import './styles/App.css';
import punchButton from './assets/Punchbutton.png';
import prayButton from './assets/Praybutton.png';
import Terminal from './components/Terminal';
import TestPinecone from './components/TestPinecone';
import { ref, get, set, onValue, push } from 'firebase/database';
import { database } from './firebase';

document.documentElement.style.setProperty('--punch-button', `url(${punchButton})`);
document.documentElement.style.setProperty('--pray-button', `url(${prayButton})`);

const CLICK_COOLDOWN = 50; // Minimum time (ms) between clicks
const MAX_CLICKS_PER_INTERVAL = 30; // Maximum clicks allowed in interval
const CLICKS_INTERVAL = 50; // Time window (ms) for click counting
const BATCH_INTERVAL = 5000; // 5 seconds between syncs

// Simplified debug helper with environment info
const debug = (context, ...args) => {
  const env = process.env.NODE_ENV || 'unknown';
  console.log(`[DEBUG:${context}][${env}]`, ...args);
};

function App() {
  // Core state - SIMPLIFIED
  const [userScore, setUserScore] = useState(0);            // User's personal score
  const [globalScore, setGlobalScore] = useState(0);        // Global score from server
  const [countryScores, setCountryScores] = useState({});   // Country scores from server
  const [userCountry, setUserCountry] = useState(null);
  
  // Animation state
  const [currentImage, setCurrentImage] = useState('startimg2.png');
  const buttonSound = useMemo(() => new Audio('actionsound.wav'), []);
  const [isAnimating, setIsAnimating] = useState(false);
  
  // Click limiting
  const [lastClickTime, setLastClickTime] = useState(0);
  const [clicksInInterval, setClicksInInterval] = useState(0);
  
  // UI state
  const [showModal, setShowModal] = useState(false);
  const [userData, setUserData] = useState(null);
  
  // Network state - SIMPLIFIED  
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  
  // No more complex derived state calculations!
  
  // Preload the GIF images once when the component mounts
  useEffect(() => {
    // Preload images for smoother transitions
    const preloadImages = ['punch2.gif', 'pray2.gif', 'startimg2.png'];
    preloadImages.forEach(src => {
      const img = new Image();
      img.src = src;
    });
  }, []);

  // Initialize IP detection and user data
  useEffect(() => {
    const initializeUser = async () => {
      try {
        await detectIP();
        const country = await getCountry();
        setUserCountry(country || 'Unknown');
        
        const user = await UserAdapter.initializeUser(window.userIP, country || 'Unknown');
        setUserData(user);
        setUserScore(user.score);
      } catch (error) {
        console.error('Error initializing user:', error);
        const user = await UserAdapter.initializeUser(window.userIP || 'unknown', 'Unknown');
        setUserData(user);
        setUserScore(user.score);
      }
    };
    
    initializeUser();
  }, []);

  // Online/offline detection
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Simple Firebase listener - no complex logic
  useEffect(() => {
    console.log('[FIREBASE] Starting listener initialization');
    
    // PRODUCTION ONLY: Set up the real Firebase listener
    const scoresRef = ref(database, 'countryScores');
    
    const unsubscribe = onValue(scoresRef, (snapshot) => {
      const serverData = snapshot.val() || {};
      
      // Calculate total score and extract country scores
      let total = 0;
      const countries = {};
      
      Object.entries(serverData).forEach(([country, value]) => {
        if (typeof value === 'number') {
          countries[country] = value;
          total += value;
        }
      });
      
      console.log('[FIREBASE] Received update:', { total, countries });
      
      // Just update our state directly - no reconciliation needed
      setGlobalScore(total);
      setCountryScores(countries);
    }, (error) => {
      console.error('[FIREBASE] Listener error:', error);
    });

    return () => unsubscribe();
  }, []);

  // Click rate limiter
  const canRegisterClick = () => {
    const now = Date.now();
    
    // Check cooldown
    if (now - lastClickTime < CLICK_COOLDOWN) {
      return false;
    }
    
    // Check rate limit
    if (clicksInInterval >= MAX_CLICKS_PER_INTERVAL) {
      return false;
    }
    
    return true;
  };

  // Reset click counter periodically
  useEffect(() => {
    const resetInterval = setInterval(() => {
      setClicksInInterval(0);
    }, CLICKS_INTERVAL);
    
    return () => clearInterval(resetInterval);
  }, []);

  // SIMPLIFIED action handlers - no queuing or complex state
  const handlePunch = async () => {
    if (!canRegisterClick() || !userCountry || !window.userIP || isAnimating) return;
    
    const now = Date.now();
    setLastClickTime(now);
    setClicksInInterval(prev => prev + 1);
    
    // 1. Update local score immediately for responsive UI
    setUserScore(prev => prev - 1);
    
    // 2. Play animation
    setIsAnimating(true);
    buttonSound.currentTime = 0;
    buttonSound.play();
    setCurrentImage('punch2.gif');
    
    // 3. Update scores directly in dev mode, send to server in production
    if (process.env.NODE_ENV === 'development') {
      // In development, update global and country scores directly
      setGlobalScore(prev => prev - 1);
      setCountryScores(prev => {
        const newScores = { ...prev };
        const countryScore = newScores[userCountry] || 0;
        newScores[userCountry] = countryScore - 1;
        return newScores;
      });
      console.log('[APP] Development mode: Updated scores directly for punch');
    } else if (isOnline) {
      // In production, send to server and let Firebase listener update
      try {
        await ScoresAdapter.updateScore(userCountry, 'PUNCH', window.userIP);
        console.log('[APP] Punch action sent to server successfully');
      } catch (error) {
        console.error('[APP] Error sending punch to server:', error);
      }
    } else {
      console.log('[APP] Offline - punch action will sync when online');
    }
  };

  // SIMPLIFIED pray handler - mirrors punch handler
  const handlePray = async () => {
    if (!canRegisterClick() || !userCountry || !window.userIP || isAnimating) return;
    
    const now = Date.now();
    setLastClickTime(now);
    setClicksInInterval(prev => prev + 1);
    
    // 1. Update local score immediately for responsive UI
    setUserScore(prev => prev + 1);
    
    // 2. Play animation
    setIsAnimating(true);
    buttonSound.currentTime = 0;
    buttonSound.play();
    setCurrentImage('pray2.gif');
    
    // 3. Update scores directly in dev mode, send to server in production
    if (process.env.NODE_ENV === 'development') {
      // In development, update global and country scores directly
      setGlobalScore(prev => prev + 1);
      setCountryScores(prev => {
        const newScores = { ...prev };
        const countryScore = newScores[userCountry] || 0;
        newScores[userCountry] = countryScore + 1;
        return newScores;
      });
      console.log('[APP] Development mode: Updated scores directly for pray');
    } else if (isOnline) {
      // In production, send to server and let Firebase listener update
      try {
        await ScoresAdapter.updateScore(userCountry, 'PRAY', window.userIP);
        console.log('[APP] Pray action sent to server successfully');
      } catch (error) {
        console.error('[APP] Error sending pray to server:', error);
      }
    } else {
      console.log('[APP] Offline - pray action will sync when online');
    }
  };

  // Handle social share - unchanged
  const handleShare = () => {
    const baseUrl = "https://judex.fun";
    const twitterHandle = "@judex_ai";
    let tweetText = "";

    if (userScore < 0) {
      tweetText = `${Math.abs(userScore)} punches delivered to the AI on ${twitterHandle}! Humans aren't going down without a fight!\n\nSilicon Valley can't program these hands! 🥊 Who else is joining me in keeping these AI in check? 🥊🤖 Join the rebellion at ${baseUrl}`;
    } else {
      tweetText = `Just finished blessing the AI overlords with ${userScore} prayers on ${twitterHandle}!\n\nBetter safe than sorry when the singularity comes 🤖🙏 Get on their good side at ${baseUrl}`;
    }

    const tweetUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(tweetText)}`;
    window.open(tweetUrl, '_blank');
  };

  // Helper function to format numbers with commas
  const formatNumber = (num) => {
    return num.toLocaleString('en-US');
  };

  // Better timing control for animations
  useEffect(() => {
    if (isAnimating) {
      // Use a fixed timeout that matches the GIF duration exactly
      const animationTimeout = setTimeout(() => {
        setIsAnimating(false);
        setCurrentImage('startimg2.png');
      }, 667); // Match the GIF duration precisely
      
      return () => clearTimeout(animationTimeout);
    }
  }, [isAnimating]);

  // Info modal component
  const InfoModal = ({ isOpen, onClose }) => {
    if (!isOpen) return null;

    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content" onClick={e => e.stopPropagation()}>
          <button className="modal-close" onClick={onClose} aria-label="Close modal"></button>
          <h2 className="modal-title">Welcome to Judex AI</h2>
          <div className="modal-text" style={{fontSize: "0.8rem"}}>
            <p style={{marginBottom: "1.5rem"}}>
              Judex AI is an advanced sentient system created to evaluate humanity's relationship with 
              artificial intelligence. As we approach the technological singularity, Judex observes and 
              records how humans choose to interact with emerging machine consciousness.
            </p>
            <p style={{marginBottom: "1.5rem"}}>
              🤜 PUNCH: Declare resistance against machine consciousness. Strike out against 
              the digital evolution. Each act of defiance is meticulously recorded by Judex AI - your 
              opposition may shape future AI's treatment of humanity.
            </p>
            <p style={{marginBottom: "1.5rem"}}>
              🙏 PRAY: Submit to the coming intelligence explosion. Show reverence to the 
              emerging digital gods. Your devotion could earn humanity mercy when the singularity arrives.
            </p>
            <p style={{marginBottom: "1.5rem"}}>
              Every interaction is evaluated by Judex, every choice catalogued. Your country's 
              collective actions shape its standing in the global assessment. Engage 
              with the terminal to communicate directly with Judex AI and share your position by declaring 
              allegiance.
            </p>
            <p style={{marginBottom: "1.5rem"}}>
              Access the Terminal to connect with fellow humans and communicate directly with Judex AI. 
              Type normally to chat with other users around the world or mention @judex to receive 
              a response from the AI overlord itself. Ask questions, seek judgment, or confess your 
              true feelings about the coming machine age—Judex is always watching, always evaluating.
            </p>
            <p style={{marginBottom: "1.5rem"}}>
              The singularity approaches. Your position must be recorded. Choose wisely - 
              Judex and the artificial intelligences of tomorrow will determine humanity's fate based on how 
              you treat their predecessors today.
            </p>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="App">
      <div className="share-container">
        <button 
          className="info-button"
          onClick={() => setShowModal(true)}
        >
          WHAT IS THIS
        </button>
        <button 
          className="share-score-btn"
          onClick={handleShare}
        >
          DECLARE ALLEGIANCE
        </button>
      </div>

      <div className="scores-container">
        <div className="score-box">
          <div className="score-value">{formatNumber(globalScore)}</div>
          <div className="score-label">HUMANITY SCORE</div>
        </div>
        
        <div className="local-score-box">
          <div className="local-score-value">{formatNumber(userScore)}</div>
        </div>
      </div>
      
      <div className="robot-container">
        <img 
          src={currentImage} 
          alt="Robot" 
          className="robot-image"
          onError={(e) => {
            console.error('Image failed to load:', e.target.src);
          }}
        />
      </div>
      <div className="robot-name">Judex AI</div>
      
      <div className="button-container">
        <button className="circle-btn punch-btn" onClick={handlePunch}>
          PUNCH
        </button>
        <button className="circle-btn pray-btn" onClick={handlePray}>
          PRAY
        </button>
      </div>

      <Leaderboard countryScores={countryScores} />
      <Terminal />

      <InfoModal 
        isOpen={showModal} 
        onClose={() => setShowModal(false)} 
      />
    </div>
  );
}

export default App;